/**
 * Identity Providers Management
 * Configure external SSO providers (Google, Microsoft, etc.)
 */

import { useState, useEffect } from 'react';
import { auth1Api } from '../../services/api';
import Icon from '../../components/common/Icon';
import './IdentityProviders.css';

const IdentityProviders = () => {
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    provider_type: '',
    client_id: '',
    client_secret: '',
    display_name: '',
    button_color: '',
    auto_create_users: true,
    auto_link_users: true,
    allowed_domains: '',
    default_role: 'user',
    // SAML-specific fields
    saml_entity_id: '',
    saml_sso_url: '',
    saml_slo_url: '',
    saml_certificate: '',
    saml_sign_requests: false,
    saml_force_authn: false,
    saml_attribute_mapping: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [metadataUrl, setMetadataUrl] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, typesRes, statsRes] = await Promise.all([
        auth1Api.listIdentityProviders(),
        auth1Api.getProviderTypes(),
        auth1Api.getSSOStats(),
      ]);
      setProviders(providersRes.providers || []);
      setProviderTypes(typesRes.providerTypes || []);
      setStats(statsRes.stats || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setSelectedType(null);
    setMetadataUrl('');
    setFormData({
      name: '',
      provider_type: '',
      client_id: '',
      client_secret: '',
      display_name: '',
      button_color: '',
      auto_create_users: true,
      auto_link_users: true,
      allowed_domains: '',
      default_role: 'user',
      saml_entity_id: '',
      saml_sso_url: '',
      saml_slo_url: '',
      saml_certificate: '',
      saml_sign_requests: false,
      saml_force_authn: false,
      saml_attribute_mapping: '',
    });
    setShowCreateModal(true);
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setFormData(prev => ({
      ...prev,
      provider_type: type.type,
      name: type.name,
      display_name: type.displayName,
      button_color: type.buttonColor,
    }));
  };

  const handleEditClick = (provider) => {
    const type = providerTypes.find(t => t.type === provider.provider_type);
    setSelectedType(type);
    setEditingProvider(provider);
    setMetadataUrl('');
    setFormData({
      name: provider.name || '',
      provider_type: provider.provider_type,
      client_id: provider.client_id || '',
      client_secret: '', // Don't pre-fill secret
      display_name: provider.display_name || '',
      button_color: provider.button_color || type?.buttonColor || '',
      auto_create_users: provider.auto_create_users ?? true,
      auto_link_users: provider.auto_link_users ?? true,
      allowed_domains: (provider.allowed_domains || []).join(', '),
      default_role: provider.default_role || 'user',
      // SAML fields
      saml_entity_id: provider.saml_entity_id || '',
      saml_sso_url: provider.saml_sso_url || '',
      saml_slo_url: provider.saml_slo_url || '',
      saml_certificate: '', // Don't pre-fill certificate for security
      saml_sign_requests: provider.saml_sign_requests ?? false,
      saml_force_authn: provider.saml_force_authn ?? false,
      saml_attribute_mapping: provider.saml_attribute_mapping
        ? JSON.stringify(provider.saml_attribute_mapping, null, 2)
        : '',
    });
    setShowCreateModal(true);
  };

  const handleImportMetadata = async () => {
    if (!metadataUrl) return;

    setFormLoading(true);
    try {
      const result = await auth1Api.importSAMLMetadata({ metadata_url: metadataUrl });
      if (result.success && result.config) {
        setFormData(prev => ({
          ...prev,
          saml_entity_id: result.config.saml_entity_id || '',
          saml_sso_url: result.config.saml_sso_url || '',
          saml_slo_url: result.config.saml_slo_url || '',
          saml_certificate: result.config.saml_certificate || '',
          saml_sign_requests: result.config.saml_sign_requests || false,
        }));
        setMetadataUrl('');
      }
    } catch (err) {
      setError(err.message || 'Failed to import IdP metadata');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const data = {
        ...formData,
        allowed_domains: formData.allowed_domains
          ? formData.allowed_domains.split(',').map(d => d.trim()).filter(Boolean)
          : null,
      };

      // Parse SAML attribute mapping JSON
      if (data.saml_attribute_mapping) {
        try {
          data.saml_attribute_mapping = JSON.parse(data.saml_attribute_mapping);
        } catch {
          setError('Invalid JSON in attribute mapping');
          setFormLoading(false);
          return;
        }
      }

      // Don't send empty secrets on update
      if (editingProvider) {
        if (!data.client_secret) delete data.client_secret;
        if (!data.saml_certificate) delete data.saml_certificate;
      }

      if (editingProvider) {
        await auth1Api.updateIdentityProvider(editingProvider.id, data);
      } else {
        await auth1Api.createIdentityProvider(data);
      }

      setShowCreateModal(false);
      setEditingProvider(null);
      setSelectedType(null);
      setMetadataUrl('');
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (provider) => {
    try {
      await auth1Api.toggleIdentityProvider(provider.id);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (provider) => {
    if (!confirm(`Are you sure you want to delete "${provider.display_name || provider.name}"? This will also remove all linked user identities.`)) {
      return;
    }

    try {
      await auth1Api.deleteIdentityProvider(provider.id);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingProvider(null);
    setSelectedType(null);
    setError(null);
  };

  if (loading) {
    return (
      <div className="idp-page">
        <div className="loading-state">
          <Icon name="loader" size="lg" className="spin" />
          <p>Loading identity providers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="idp-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Identity Providers</h1>
          <p className="page-subtitle">
            Configure external SSO providers for user authentication
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleCreateClick}>
            <Icon name="plus" size="sm" />
            Add Provider
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <Icon name="alert-circle" />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon providers">
              <Icon name="key" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.providers?.total || 0}</div>
              <div className="stat-label">Total Providers</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon active">
              <Icon name="check-circle" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.providers?.enabled || 0}</div>
              <div className="stat-label">Active</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon users">
              <Icon name="users" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalLinkedUsers || 0}</div>
              <div className="stat-label">Linked Users</div>
            </div>
          </div>
        </div>
      )}

      {/* Providers Grid */}
      {providers.length === 0 ? (
        <div className="empty-state">
          <Icon name="shield" size="xl" />
          <h4>No Identity Providers</h4>
          <p>Add a provider to enable SSO login for your users</p>
          <button className="btn btn-primary" onClick={handleCreateClick}>
            <Icon name="plus" size="sm" />
            Add First Provider
          </button>
        </div>
      ) : (
        <div className="providers-grid">
          {providers.map(provider => {
            const typeInfo = providerTypes.find(t => t.type === provider.provider_type);
            const providerStats = stats?.byProvider?.find(p => p.id === provider.id);

            return (
              <div
                key={provider.id}
                className={`provider-card ${provider.is_enabled ? '' : 'disabled'}`}
              >
                <div className="provider-header">
                  <div className="provider-logo">
                    {provider.logo_url || typeInfo?.logoUrl ? (
                      <img
                        src={provider.logo_url || typeInfo?.logoUrl}
                        alt={provider.display_name || provider.name}
                      />
                    ) : (
                      <div
                        className="logo-placeholder"
                        style={{ backgroundColor: provider.button_color || typeInfo?.buttonColor }}
                      >
                        {(provider.display_name || provider.name).charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="provider-info">
                    <h3>{provider.display_name || provider.name}</h3>
                    <span className="provider-type">{typeInfo?.name || provider.provider_type}</span>
                  </div>
                  <div className="provider-status">
                    <span className={`status-badge ${provider.is_enabled ? 'active' : 'inactive'}`}>
                      {provider.is_enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="provider-body">
                  <div className="provider-meta">
                    <div className="meta-item">
                      <Icon name="users" size="sm" />
                      <span>{providerStats?.linkedUsers || 0} linked users</span>
                    </div>
                    {provider.auto_create_users && (
                      <div className="meta-item">
                        <Icon name="user-plus" size="sm" />
                        <span>Auto-create users</span>
                      </div>
                    )}
                    {provider.allowed_domains?.length > 0 && (
                      <div className="meta-item">
                        <Icon name="globe" size="sm" />
                        <span>{provider.allowed_domains.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {provider.client_id && (
                    <div className="client-id">
                      <span className="label">Client ID:</span>
                      <code>{provider.client_id.substring(0, 20)}...</code>
                    </div>
                  )}
                </div>

                <div className="provider-footer">
                  <button
                    className="btn btn-text"
                    onClick={() => handleToggle(provider)}
                  >
                    {provider.is_enabled ? 'Disable' : 'Enable'}
                  </button>
                  <div className="footer-actions">
                    <button
                      className="btn btn-icon"
                      onClick={() => handleEditClick(provider)}
                      title="Edit"
                    >
                      <Icon name="edit" size="sm" />
                    </button>
                    <button
                      className="btn btn-icon danger"
                      onClick={() => handleDelete(provider)}
                      title="Delete"
                    >
                      <Icon name="trash-2" size="sm" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProvider ? 'Edit Provider' : 'Add Identity Provider'}</h3>
              <button className="close-btn" onClick={closeModal}>
                <Icon name="x" />
              </button>
            </div>

            <div className="modal-body">
              {!selectedType && !editingProvider ? (
                // Provider type selection
                <div className="provider-type-grid">
                  <h4>Select Provider Type</h4>
                  {providerTypes.filter(t => !t.comingSoon).map(type => (
                    <button
                      key={type.type}
                      className="provider-type-option"
                      onClick={() => handleSelectType(type)}
                    >
                      <div className="type-logo">
                        {type.logoUrl ? (
                          <img src={type.logoUrl} alt={type.name} />
                        ) : (
                          <div
                            className="logo-placeholder"
                            style={{ backgroundColor: type.buttonColor }}
                          >
                            {type.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="type-info">
                        <span className="type-name">{type.name}</span>
                        <span className="type-desc">{type.displayName}</span>
                      </div>
                    </button>
                  ))}

                  <div className="coming-soon-section">
                    <h5>Coming Soon</h5>
                    <div className="coming-soon-providers">
                      {providerTypes.filter(t => t.comingSoon).map(type => (
                        <div key={type.type} className="provider-type-option disabled">
                          <div className="type-logo">
                            {type.logoUrl ? (
                              <img src={type.logoUrl} alt={type.name} />
                            ) : (
                              <div
                                className="logo-placeholder"
                                style={{ backgroundColor: type.buttonColor }}
                              >
                                {type.name.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="type-info">
                            <span className="type-name">{type.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Configuration form
                <form onSubmit={handleSubmit}>
                  <div className="selected-type">
                    <div className="type-logo">
                      {selectedType?.logoUrl ? (
                        <img src={selectedType.logoUrl} alt={selectedType?.name} />
                      ) : (
                        <div
                          className="logo-placeholder"
                          style={{ backgroundColor: selectedType?.buttonColor }}
                        >
                          {selectedType?.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="type-info">
                      <strong>{selectedType?.name}</strong>
                      {selectedType?.documentation && (
                        <a
                          href={selectedType.documentation}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="docs-link"
                        >
                          <Icon name="external-link" size="sm" />
                          Documentation
                        </a>
                      )}
                    </div>
                    {!editingProvider && (
                      <button
                        type="button"
                        className="btn btn-text"
                        onClick={() => setSelectedType(null)}
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {/* OAuth Credentials - for non-SAML providers */}
                  {selectedType?.type !== 'saml' && (
                    <div className="form-section">
                      <h4>OAuth Credentials</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Client ID *</label>
                          <input
                            type="text"
                            value={formData.client_id}
                            onChange={e => setFormData(prev => ({ ...prev, client_id: e.target.value }))}
                            placeholder="Your OAuth client ID"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Client Secret {editingProvider ? '(leave blank to keep)' : '*'}</label>
                          <input
                            type="password"
                            value={formData.client_secret}
                            onChange={e => setFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                            placeholder={editingProvider ? '••••••••' : 'Your OAuth client secret'}
                            required={!editingProvider}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SAML Configuration */}
                  {selectedType?.type === 'saml' && (
                    <div className="form-section">
                      <h4>SAML Configuration</h4>

                      {/* Metadata Import */}
                      <div className="form-group">
                        <label>Import from Metadata URL (optional)</label>
                        <div className="metadata-import">
                          <input
                            type="url"
                            value={metadataUrl}
                            onChange={e => setMetadataUrl(e.target.value)}
                            placeholder="https://idp.example.com/saml/metadata"
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleImportMetadata}
                            disabled={!metadataUrl || formLoading}
                          >
                            Import
                          </button>
                        </div>
                        <small className="form-hint">Auto-fill configuration from IdP metadata URL</small>
                      </div>

                      <div className="form-group">
                        <label>IdP Entity ID *</label>
                        <input
                          type="text"
                          value={formData.saml_entity_id}
                          onChange={e => setFormData(prev => ({ ...prev, saml_entity_id: e.target.value }))}
                          placeholder="https://idp.example.com/saml/metadata"
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>SSO URL *</label>
                          <input
                            type="url"
                            value={formData.saml_sso_url}
                            onChange={e => setFormData(prev => ({ ...prev, saml_sso_url: e.target.value }))}
                            placeholder="https://idp.example.com/saml/sso"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>SLO URL (optional)</label>
                          <input
                            type="url"
                            value={formData.saml_slo_url}
                            onChange={e => setFormData(prev => ({ ...prev, saml_slo_url: e.target.value }))}
                            placeholder="https://idp.example.com/saml/slo"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>IdP X.509 Certificate {editingProvider ? '(leave blank to keep)' : '*'}</label>
                        <textarea
                          value={formData.saml_certificate}
                          onChange={e => setFormData(prev => ({ ...prev, saml_certificate: e.target.value }))}
                          placeholder="-----BEGIN CERTIFICATE-----
MIIDpTCCAo2gAwIBAgI...
-----END CERTIFICATE-----"
                          rows={6}
                          required={!editingProvider}
                        />
                        <small className="form-hint">The IdP's signing certificate in PEM format</small>
                      </div>

                      <div className="toggle-options">
                        <label className="toggle-option">
                          <input
                            type="checkbox"
                            checked={formData.saml_sign_requests}
                            onChange={e => setFormData(prev => ({ ...prev, saml_sign_requests: e.target.checked }))}
                          />
                          <span className="toggle-label">
                            <strong>Sign AuthnRequests</strong>
                            <small>Sign outgoing authentication requests</small>
                          </span>
                        </label>
                        <label className="toggle-option">
                          <input
                            type="checkbox"
                            checked={formData.saml_force_authn}
                            onChange={e => setFormData(prev => ({ ...prev, saml_force_authn: e.target.checked }))}
                          />
                          <span className="toggle-label">
                            <strong>Force Re-authentication</strong>
                            <small>Always require fresh authentication at IdP</small>
                          </span>
                        </label>
                      </div>

                      <div className="form-group">
                        <label>Attribute Mapping (JSON, optional)</label>
                        <textarea
                          value={formData.saml_attribute_mapping}
                          onChange={e => setFormData(prev => ({ ...prev, saml_attribute_mapping: e.target.value }))}
                          placeholder='{"email": ["mail", "emailAddress"], "firstName": ["givenName"]}'
                          rows={3}
                        />
                        <small className="form-hint">Custom mapping of SAML attributes to user fields</small>
                      </div>
                    </div>
                  )}

                  <div className="form-section">
                    <h4>Display Settings</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Display Name</label>
                        <input
                          type="text"
                          value={formData.display_name}
                          onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                          placeholder="e.g., Sign in with Google"
                        />
                      </div>
                      <div className="form-group">
                        <label>Button Color</label>
                        <div className="color-input">
                          <input
                            type="color"
                            value={formData.button_color || '#4285F4'}
                            onChange={e => setFormData(prev => ({ ...prev, button_color: e.target.value }))}
                          />
                          <input
                            type="text"
                            value={formData.button_color}
                            onChange={e => setFormData(prev => ({ ...prev, button_color: e.target.value }))}
                            placeholder="#4285F4"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h4>User Settings</h4>
                    <div className="toggle-options">
                      <label className="toggle-option">
                        <input
                          type="checkbox"
                          checked={formData.auto_create_users}
                          onChange={e => setFormData(prev => ({ ...prev, auto_create_users: e.target.checked }))}
                        />
                        <span className="toggle-label">
                          <strong>Auto-create users</strong>
                          <small>Create new users automatically on first SSO login</small>
                        </span>
                      </label>
                      <label className="toggle-option">
                        <input
                          type="checkbox"
                          checked={formData.auto_link_users}
                          onChange={e => setFormData(prev => ({ ...prev, auto_link_users: e.target.checked }))}
                        />
                        <span className="toggle-label">
                          <strong>Auto-link by email</strong>
                          <small>Link SSO identity to existing user with matching email</small>
                        </span>
                      </label>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Allowed Domains (optional)</label>
                        <input
                          type="text"
                          value={formData.allowed_domains}
                          onChange={e => setFormData(prev => ({ ...prev, allowed_domains: e.target.value }))}
                          placeholder="example.com, company.org"
                        />
                        <small className="form-hint">Comma-separated. Leave empty to allow all domains.</small>
                      </div>
                      <div className="form-group">
                        <label>Default Role</label>
                        <select
                          value={formData.default_role}
                          onChange={e => setFormData(prev => ({ ...prev, default_role: e.target.value }))}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {selectedType && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={formLoading || (
                    selectedType?.type === 'saml'
                      ? !formData.saml_entity_id || !formData.saml_sso_url || (!editingProvider && !formData.saml_certificate)
                      : !formData.client_id || (!editingProvider && !formData.client_secret)
                  )}
                >
                  {formLoading ? (
                    <>
                      <Icon name="loader" size="sm" className="spin" />
                      Saving...
                    </>
                  ) : editingProvider ? (
                    'Save Changes'
                  ) : (
                    'Create Provider'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentityProviders;
